import React, { useMemo, useRef, useState } from "react";
import './AdImageGallery.css';
import { WASSLA_W_LOGO as wLogo } from "../assets/wasslaLogoData";

/**
 * Premium Boutique Gallery
 * - Swipe left/right
 * - Dots indicator
 * - Thumbnails row
 * - Tap image => full-screen zoom modal
 * - Safe on mobile: no overflow-x
 */
export function AdImageGallery({ images = [], title = "", showThumbnails = true, hero = false }) {
  const safeImages = useMemo(
    () => (Array.isArray(images) ? images.filter(Boolean) : []),
    [images]
  );

  const [active, setActive] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  // iter353 — broken-image fallback + retry for the main slide
  const [mainError, setMainError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  const trackRef = useRef(null);
  const startX = useRef(null);
  const isDragging = useRef(false);

  const hasImages = safeImages.length > 0;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const go = (idx) => {
    setMainError(false);
    setActive(clamp(idx, 0, safeImages.length - 1));
  };

  const onTouchStart = (e) => {
    if (!hasImages) return;
    startX.current = e.touches[0].clientX;
    isDragging.current = true;
  };

  const onTouchMove = (e) => {
    if (!hasImages || !isDragging.current) return;
    // nothing needed; we resolve on end
  };

  const onTouchEnd = (e) => {
    if (!hasImages || !isDragging.current) return;
    const endX = e.changedTouches[0].clientX;
    const delta = endX - startX.current;
    isDragging.current = false;

    // swipe threshold
    if (Math.abs(delta) > 40) {
      if (delta < 0) go(active + 1);
      else go(active - 1);
    }
  };

  return (
    <div className={`pg-wrap ${hero ? "pg-wrap-hero" : ""}`} aria-label="Ad images gallery" data-testid="ad-image-gallery-wrap">
      {/* MAIN */}
      <div
        className={`pg-main ${hero ? "pg-main-hero" : ""}`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        role="button"
        tabIndex={0}
        data-testid="ad-image-gallery-main"
        onClick={() => hasImages && setZoomOpen(true)}
        onKeyDown={(e) => {
          if (!hasImages) return;
          if (e.key === "Enter") setZoomOpen(true);
          if (e.key === "ArrowRight") go(active + 1);
          if (e.key === "ArrowLeft") go(active - 1);
        }}
      >
        {/* gradient edges (premium) */}
        <div className="pg-edge pg-edge-l" aria-hidden="true" />
        <div className="pg-edge pg-edge-r" aria-hidden="true" />

        {!hasImages ? (
          <>
            <div className="pg-placeholder">
              <img
                className="pg-ph-logo"
                src={wLogo}
                alt=""
                draggable={false}
              />
              <div className="pg-ph-text">No image available</div>
            </div>
            <div className="pg-index-pill">1/1</div>
            <div className="pg-dots" aria-hidden="true">
              <span className="pg-dot pg-dot-active" />
            </div>
          </>
        ) : mainError ? (
          <>
            <div className="pg-placeholder" data-testid="ad-gallery-image-error">
              <img
                className="pg-ph-logo"
                src={wLogo}
                alt=""
                draggable={false}
              />
              <button
                type="button"
                className="pg-retry-btn"
                data-testid="ad-gallery-retry-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setMainError(false);
                  setRetryTick((n) => n + 1);
                }}
              >
                ↻
              </button>
            </div>
            <div className="pg-index-pill">
              {active + 1}/{safeImages.length}
            </div>
          </>
        ) : (
          <>
            <img
              key={`${active}-${retryTick}`}
              className="pg-main-img"
              src={retryTick > 0
                ? `${safeImages[active]}${safeImages[active].includes('?') ? '&' : '?'}wretry=${retryTick}`
                : safeImages[active]}
              alt={title ? `${title} - ${active + 1}` : `Image ${active + 1}`}
              loading="eager"
              draggable={false}
              onError={() => setMainError(true)}
            />

            {/* soft vignette */}
            <div className="pg-vignette" aria-hidden="true" />

            {/* index pill */}
            <div className="pg-index-pill">
              {active + 1}/{safeImages.length}
            </div>

            {/* dots */}
            <div className="pg-dots" aria-hidden="true">
              {safeImages.slice(0, 8).map((_, i) => (
                <span
                  key={i}
                  className={`pg-dot ${i === active ? "pg-dot-active" : ""}`}
                />
              ))}
              {safeImages.length > 8 && <span className="pg-dot-more">…</span>}
            </div>
          </>
        )}
      </div>

      {/* THUMBNAILS */}
      {showThumbnails && hasImages && safeImages.length > 1 && (
        <div className="pg-thumb-row" ref={trackRef}>
          {safeImages.map((src, i) => (
            <button
              key={`${src}-${i}`}
              className={`pg-thumb ${i === active ? "pg-thumb-active" : ""}`}
              onClick={() => go(i)}
              aria-label={`Open image ${i + 1}`}
            >
              <img
                className="pg-thumb-img"
                src={src}
                alt=""
                loading="lazy"
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}

      {/* ZOOM MODAL */}
      {zoomOpen && hasImages && (
        <ZoomModal
          images={safeImages}
          active={active}
          setActive={setActive}
          onClose={() => setZoomOpen(false)}
          title={title}
        />
      )}
    </div>
  );
}

function ZoomModal({ images, active, setActive, onClose, title }) {
  const [scale, setScale] = useState(1);
  const lastTouch = useRef(null);

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
  const go = (idx) => setActive(clamp(idx, 0, images.length - 1));

  const resetZoom = () => setScale(1);

  const onWheel = (e) => {
    e.preventDefault();
    const next = scale + (e.deltaY < 0 ? 0.08 : -0.08);
    setScale(clamp(next, 1, 3));
  };

  // very light pinch approximation (two-finger)
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouch.current = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && lastTouch.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const delta = dist - lastTouch.current;
      lastTouch.current = dist;
      setScale((s) => clamp(s + delta / 250, 1, 3));
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) lastTouch.current = null;
  };

  return (
    <div className="pg-modal" role="dialog" aria-modal="true">
      <div className="pg-modal-top">
        <button className="pg-modal-btn" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="pg-modal-title">
          {title ? title : "Preview"} • {active + 1}/{images.length}
        </div>

        <button
          className="pg-modal-btn"
          onClick={resetZoom}
          aria-label="Reset zoom"
        >
          ⟲
        </button>
      </div>

      <div className="pg-modal-body">
        <button
          className="pg-nav-btn pg-nav-left"
          onClick={() => {
            resetZoom();
            go(active - 1);
          }}
          aria-label="Previous image"
        >
          ‹
        </button>

        <div
          className="pg-zoom-area"
          onWheel={onWheel}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <img
            className="pg-zoom-img"
            src={images[active]}
            alt={title ? `${title} - ${active + 1}` : `Image ${active + 1}`}
            style={{ transform: `scale(${scale})` }}
            draggable={false}
          />
        </div>

        <button
          className="pg-nav-btn pg-nav-right"
          onClick={() => {
            resetZoom();
            go(active + 1);
          }}
          aria-label="Next image"
        >
          ›
        </button>
      </div>
    </div>
  );
}
